import {
  CreateCustomerFormValues,
  CustomerFormPrefixes,
} from '@components/CreateCustomer/components/CreateCustomerForm/CreateCustomerFormDefinitions';
import AddressSearchInput from './components/AddressSearchInput/AddressSearchInput';
import AddressForm from './components/AddressForm/AddressForm';
import formatAddress from '@lib/utils/formatAddress';
import { useFormContext } from 'react-hook-form';
import AddressFormManagerContext, {
  AddressFormManagerContextType,
  SelectedSuggestedAddressType,
} from './context/AddressFormManagerContext';
import { useCallback, useMemo, useState } from 'react';
import {
  AddresFormPlaceholders,
  FormAddressType,
} from './components/AddressForm';
import classNames from 'classnames';
import useSuggestedAddressSearch from './data/useSuggestedAddressSearch';

interface AddressManagerProps {
  formFieldPrefix: CustomerFormPrefixes;
  showFullFormByDefault?: boolean;
  hideAddressVerificationAndSearch?: boolean;
  placeholders?: AddresFormPlaceholders;
}

/**
 * This component is responsible for managing the overall state of the address form and rendering the address form components.
 *
 * Renders an input field for searching for an address. When the user types in the input field, a list of suggested addresses will be displayed.
 * If the user selects a suggested address, the full form will appear and the address fields will be populated with the selected address.
 * If the user clicks the manual address entry button, the full address form will be displayed.
 *
 */
const AddressFormManager = ({
  formFieldPrefix,
  showFullFormByDefault = false,
  hideAddressVerificationAndSearch = false,
  placeholders,
}: AddressManagerProps) => {
  const [showFullForm, setShowFullForm] = useState(showFullFormByDefault);
  const [selectedSuggestedAddress, setSelectedSuggestedAddress] =
    useState<SelectedSuggestedAddressType>(undefined);

  const { getValues, setValue, watch } =
    useFormContext<CreateCustomerFormValues>();

  const formAddress = watch(`${formFieldPrefix}.address`);

  const {
    suggestedAddresses,
    loadingSuggestedAddresses,
    fetchSuggestedAddressDetails,
  } = useSuggestedAddressSearch(formAddress);

  /**
   * This function is called when the user clicks on a suggested address or clicks the manual address entry button.
   * Address will be passed in the case that the suggested address is clicked.
   */
  const handleShowFullForm = useCallback(
    (address?: FormAddressType) => {
      if (address) {
        setValue(`${formFieldPrefix}.address.street`, address.street, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${formFieldPrefix}.address.unit`, address.unit, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${formFieldPrefix}.address.city`, address.city, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${formFieldPrefix}.address.state`, address.state, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${formFieldPrefix}.address.zip`, address.zip, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${formFieldPrefix}.address.country`, address.country, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${formFieldPrefix}.address.latitude`, address.latitude, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${formFieldPrefix}.address.longitude`, address.longitude, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
      setShowFullForm(true);
    },
    [formFieldPrefix, setValue],
  );

  /**
   * This function is called when the user clicks the hide form button. It will set the address.street to the formatted address and clear the other fields.
   */
  const handleHideFullForm = useCallback(() => {
    const address = getValues(`${formFieldPrefix}.address`);

    if (address) {
      const formattedAddress = formatAddress(address);
      setValue(`${formFieldPrefix}.address.street`, formattedAddress);
    }

    setValue(`${formFieldPrefix}.address.unit`, '');
    setValue(`${formFieldPrefix}.address.city`, '');
    setValue(`${formFieldPrefix}.address.state`, '');
    setValue(`${formFieldPrefix}.address.zip`, '');
    setValue(`${formFieldPrefix}.address.latitude`, '');
    setValue(`${formFieldPrefix}.address.longitude`, '');
    setShowFullForm(false);
  }, [formFieldPrefix, getValues, setValue]);

  // Checks if the suggested address is the same as the inputs in the form
  const isSuggestedAddressUsed = useMemo(() => {
    if (!selectedSuggestedAddress) return false;

    return (
      formAddress?.street === selectedSuggestedAddress.street &&
      formAddress?.unit === selectedSuggestedAddress.unit &&
      formAddress?.city === selectedSuggestedAddress.city &&
      formAddress?.state === selectedSuggestedAddress.state &&
      formAddress?.country === selectedSuggestedAddress.country &&
      formAddress?.zip === selectedSuggestedAddress.zip
    );
  }, [
    formAddress?.city,
    formAddress?.country,
    formAddress?.state,
    formAddress?.street,
    formAddress?.unit,
    formAddress?.zip,
    selectedSuggestedAddress,
  ]);

  const handleSuggestedAddressClick = useCallback(
    async (placeId: string) => {
      const address = await fetchSuggestedAddressDetails(placeId);
      const typedAddress = {
        street: address.street,
        unit: address.unit ?? '',
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country ?? '',
        latitude: address.latitude ? address.latitude.toString() : '',
        longitude: address.longitude ? address.longitude.toString() : '',
      };
      setSelectedSuggestedAddress(typedAddress);
      handleShowFullForm(typedAddress);
    },
    [fetchSuggestedAddressDetails, handleShowFullForm],
  );

  const addressFormManagerContextValues: AddressFormManagerContextType =
    useMemo(
      () => ({
        suggestedAddresses,
        loadingSuggestedAddresses,
        selectedSuggestedAddress,
        handleSuggestedAddressClick,
        isSuggestedAddressUsed,
        hideFullForm: handleHideFullForm,
        formFieldPrefix,
        showFullForm: handleShowFullForm,
      }),
      [
        suggestedAddresses,
        loadingSuggestedAddresses,
        handleSuggestedAddressClick,
        isSuggestedAddressUsed,
        handleHideFullForm,
        formFieldPrefix,
        handleShowFullForm,
        selectedSuggestedAddress,
      ],
    );

  return (
    <AddressFormManagerContext.Provider value={addressFormManagerContextValues}>
      <div
        className={classNames({ 'm-b-5': !hideAddressVerificationAndSearch })}
      >
        {!showFullForm ? (
          <AddressSearchInput />
        ) : (
          <AddressForm
            hideAddressVerificationAndSearch={hideAddressVerificationAndSearch}
            placeholders={placeholders}
          />
        )}
      </div>
    </AddressFormManagerContext.Provider>
  );
};

export default AddressFormManager;
